<template>
  <fn-form @submit="onSubmit">
    <fn-form-errors :errors="errors" />

    <fn-paragraph
      v-if="requiredNotice"
      small
      color="grey"
      align-right
      class="form-required-notice"
    >
      {{ $t('forms.global.requiredFields') }}
    </fn-paragraph>

    <fn-form-group>
      <fn-form-section>
        <fn-form-field-info-wrapper
          :class="{
            'form-field-info-wrapper--full-width': fullWidthInputs,
          }"
          :no-side-margin="fullWidthInputs"
          :no-flex-side="fullWidthInputs"
        >
          <template #default>
            <fn-form-field-input
              id="register-userData-email"
              :class="{ 'form-input-email--full-width': fullWidthInputs }"
              name="email"
              :label="$t('forms.checkout.fields.email.label')"
              :maxlength="$t('forms.maxLength.email')"
              type="email"
              :rules="{
                required: true,
                email: true,
                max: $t('forms.maxLength.email'),
              }"
              :full-width="fullWidthInputs"
            />
          </template>
          <template #side>
            <fn-info-i
              class="ef-info"
              :aria-label-open-button="
                $t('accessibility.form.openAdditionalInfoDialog', { field: $t('forms.checkout.fields.email.label') })
              "
              :aria-label-close-button="
                $t('accessibility.form.closeAdditionalInfoDialog', { field: $t('forms.checkout.fields.email.label') })
              "
            >
              <fn-headline
                type="h4"
                size="h5"
              >
                {{ $t('forms.checkout.fields.email.info.headline') }}
              </fn-headline>
              <fn-paragraph>
                {{ $t('forms.checkout.fields.email.info.text') }}
              </fn-paragraph>
            </fn-info-i>
          </template>
        </fn-form-field-info-wrapper>
      </fn-form-section>
    </fn-form-group>

    <fn-form-group>
      <fn-form-section>
        <fn-form-field-password
          id="register-userData-password"
          name="password"
          :label="$t('forms.checkout.fields.password.label')"
          :rules="{
            min: 8,
            max: 32,
            min_1_number: true,
            min_1_large_alpha: true,
            min_1_small_alpha: true,
            min_1_special_char: true,
            required: true,
          }"
          :full-width="fullWidthInputs"
          autocomplete="new-password"
          @focus="showLegalConsent = true"
          @update:value="showLegalConsent = true"
        />
      </fn-form-section>
    </fn-form-group>

    <fn-form-group v-show="showLegalConsent">
      <fn-form-section>
        <fn-form-field-checkbox
          v-if="friendsLight"
          id="register-friends-doi"
          name="friendsTos"
          type="checkbox"
          required
          show-mandatory
          :rules="{
            required: true,
          }"
        >
          <template #afterLabel>
            <i18n-t
              keypath="forms.friends.registration.consent"
              tag="p"
              class="checkbox-label-after--privacynotice"
            >
              <template #terms>
                <fn-link
                  inline
                  target="_blank"
                  :to="$t('forms.friends.registration.tosUrl')"
                >
                  {{ $t('forms.friends.registration.terms') }}
                </fn-link>
              </template>
            </i18n-t>
          </template>
        </fn-form-field-checkbox>

        <fn-form-field-checkbox
          id="register-userData-doi"
          name="legal"
          label=""
          type="checkbox"
          required
          show-mandatory
          :rules="{
            required: true,
          }"
        >
          <template #afterLabel>
            <i18n-t
              keypath="forms.checkout.account.legal"
              tag="p"
              class="checkbox-label-after--privacynotice"
            >
              <template #partnerNotice>
                <fn-link
                  inline
                  target="_blank"
                  :to="$t('forms.checkout.account.partnerUrl')"
                >
                  {{ $t('forms.checkout.account.partnerNotice') }}
                </fn-link>
              </template>
              <template #privacyNotice>
                <fn-link
                  inline
                  target="_blank"
                  :to="$t('forms.checkout.account.privacyUrl')"
                >
                  {{ $t('forms.checkout.account.privacyNotice') }}
                </fn-link>
              </template>
            </i18n-t>
          </template>
        </fn-form-field-checkbox>

        <fn-form-field-checkbox
          v-if="friendsLight && !hasNewsletterSubscription"
          id="register-newsletter-doi"
          name="newsletterConsent"
          type="checkbox"
        >
          <template #afterLabel>
            <i18n-t
              keypath="forms.newsletter.privacyNotice.text"
              tag="p"
              class="checkbox-label-after--privacynotice"
            >
              <template #privacyNotice>
                <fn-link
                  inline
                  target="_blank"
                  :to="$t('forms.newsletter.privacyNotice.privacyUrl')"
                >
                  {{ $t('forms.newsletter.privacyNotice.privacyNotice') }}
                </fn-link>
              </template>
            </i18n-t>
          </template>
        </fn-form-field-checkbox>
      </fn-form-section>
    </fn-form-group>

    <fn-form-group v-if="!buttonDisabled">
      <fn-form-section>
        <fn-button
          id="register-userData-submit"
          secondary
          submit
          full-width
          :orientation-green="salon"
        >
          {{ friendsLight ? $t('registration.buttonAlt') : $t('registration.button') }}
        </fn-button>
      </fn-form-section>
    </fn-form-group>

    <fn-form-group v-if="loginRecaptchaEnabled || registrationRecaptchaEnabled || newsletterRecaptchaEnabled">
      <fn-form-section>
        <fn-recaptcha
          ref="recaptcha"
          no-margin
          small-print
        />
        <fn-recaptcha
          v-if="loginRecaptchaEnabled"
          ref="loginRecaptcha"
          :visible="false"
        />
        <fn-recaptcha
          v-if="registrationRecaptchaEnabled"
          ref="registrationRecaptcha"
          :visible="false"
        />
        <fn-recaptcha
          v-if="friendsLight && newsletterRecaptchaEnabled && !hasNewsletterSubscription"
          ref="newsletterRecaptcha"
        />
      </fn-form-section>
    </fn-form-group>
  </fn-form>
</template>

<script>
import { actions } from '@/utils/recaptcha';

export default defineComponent({
  props: {
    errors: {
      type: Object,
      default: () => ({}),
    },
    buttonDisabled: {
      type: Boolean,
    },
    requiredNotice: {
      type: Boolean,
    },
    legalHidden: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    fullWidthInputs: {
      type: Boolean,
    },
    friendsLight: {
      type: Boolean,
    },
    salon: {
      type: Boolean,
    },
  },
  emits: ['submit'],
  data: () => ({
    fields: {
      loginRecaptchaResponse: null,
      registrationRecaptchaResponse: null,
      newsletterRecaptchaResponse: null,
    },
    showLegalConsent: false,
  }),
  computed: {
    ...mapState(useMainStore, {
      loginRecaptchaEnabled: 'getLoginRecaptchaEnabled',
      registrationRecaptchaEnabled: 'getRegistrationRecaptchaEnabled',
      newsletterRecaptchaEnabled: 'getNewsletterRecaptchaEnabled',
    }),
    ...mapState(useNewsletterStore, {
      getNewsletterSubscription: 'newsletterSubscription',
      hasNewsletterSubscription: 'hasNewsletterSubscription',
    }),
  },
  mounted() {
    this.showLegalConsent = !this.legalHidden;
  },
  methods: {
    async onSubmit({ values }) {
      if (this.loginRecaptchaEnabled) {
        this.fields.loginRecaptchaResponse = (
          await this.$refs.loginRecaptcha.execute(actions.login)
        )?.recaptchaResponse;
      }

      if (this.registrationRecaptchaEnabled) {
        this.fields.registrationRecaptchaResponse = (
          await this.$refs.registrationRecaptcha.execute(actions.register)
        )?.recaptchaResponse;
      }

      if (this.newsletterRecaptchaEnabled) {
        this.fields.newsletterRecaptchaResponse = (
          await this.$refs.recaptcha.execute(actions.newsletter)
        )?.recaptchaResponse;
      }

      if (this.friendsLight) {
        this.fields.friendsLight = true;
      }

      this.$emit('submit', {
        ...values,
        ...this.fields,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.form-field-info-wrapper--full-width {
  :deep(.ffiw-side) {
    position: relative;
    top: 12px;
    right: 12px;
  }
}

:deep(.form-input-email) {
  width: calc(100% - 32px);

  .form-input-email--full-width {
    width: 100%;
  }
}

:deep(.checkbox) {
  .checkbox-label-after {
    margin-left: 0;
  }

  .checkbox-label-after--privacynotice {
    display: inline;
  }
}

.form-required-notice {
  margin-right: $pad * 2;
  margin-bottom: math.div($space, 4);
}
</style>
